import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getBrandThemes, getBranding } from '../lib/api';

const BrandContext = createContext({
  brand: null,
  activeTheme: null,
  loading: true,
  refreshBrand: () => {},
});

export const useBrand = () => useContext(BrandContext);

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(null);
  const [activeTheme, setActiveTheme] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshBrand = useCallback(async () => {
    try {
      const [brandingRes, themesRes] = await Promise.all([
        getBranding().catch(() => ({ data: null })),
        getBrandThemes().catch(() => ({ data: [] })),
      ]);

      const branding = brandingRes.data || {};
      const themes = themesRes.data || [];
      const active = themes.find(t => t.is_active) || null;

      setBrand(branding);
      setActiveTheme(active);

      // Inject theme CSS variables into :root
      injectThemeVars(active, branding);
    } catch (err) {
      console.error('[Brand] Failed to load branding:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshBrand();
  }, [refreshBrand]);

  return (
    <BrandContext.Provider value={{ brand, activeTheme, loading, refreshBrand }}>
      {children}
    </BrandContext.Provider>
  );
}

function hslFromHex(hex) {
  // Convert hex to HSL string for CSS variable injection
  if (!hex || !hex.startsWith('#')) return null;
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function injectThemeVars(theme, branding) {
  if (!theme && !branding) return;

  const root = document.documentElement;
  const source = theme || branding;

  // Map tenant brand colors to CSS custom properties
  const primaryHsl = hslFromHex(source.primary_color || source.accent_color);
  const accentHsl = hslFromHex(source.accent_color || source.primary_color);
  const secondaryHsl = hslFromHex(source.secondary_color);

  if (accentHsl) {
    root.style.setProperty('--accent', accentHsl);
    root.style.setProperty('--ring', accentHsl);
    root.style.setProperty('--chart-1', accentHsl);
  }

  if (primaryHsl) {
    root.style.setProperty('--brand-primary', primaryHsl);
    root.style.setProperty('--primary', primaryHsl);
  }

  if (secondaryHsl) {
    root.style.setProperty('--brand-secondary', secondaryHsl);
  }

  // Store brand metadata as CSS custom properties for components
  if (source.company_name) {
    root.style.setProperty('--brand-company', `"${source.company_name}"`);
  }
  if (source.logo_url) {
    root.style.setProperty('--brand-logo-url', `url(${source.logo_url})`);
  }
  if (source.font_family) {
    root.style.setProperty('--brand-font', source.font_family);
  }
}
